import React from 'react';
import { Box, Tabs, Tab, Tooltip } from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';

export type ViewSubTab = 'columns' | 'models' | 'pivots';

interface ViewSubTabsProps {
  value: ViewSubTab;
  onChange: (next: ViewSubTab) => void;
  /** Whether a view is currently selected. Models + Pivots need one; Columns
      is always reachable so the user is never stranded. */
  hasView: boolean;
}

const NEEDS_VIEW = 'Pick a view in the left nav first.';

// MUI Tabs discovers direct-child <Tab> elements via cloneElement to wire
// up value/onChange. Wrapping a <Tab> in <Tooltip><span> breaks that
// coupling — the Tab loses its click handler and does nothing.
// The safe pattern: put the tooltip INSIDE the label, so <Tab> stays
// a direct child of <Tabs>.
const labelWithTooltip = (text: string, hint: string) =>
  hint ? (
    <Tooltip title={hint} placement="bottom" arrow>
      <span>{text}</span>
    </Tooltip>
  ) : (
    text
  );

const ViewSubTabs: React.FC<ViewSubTabsProps> = ({ value, onChange, hasView }) => {
  const disabledHint = hasView ? '' : NEEDS_VIEW;
  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: 'background.paper' }}>
      <Tabs
        value={value}
        onChange={(_e, v) => onChange(v as ViewSubTab)}
        aria-label="view manager sub-tabs"
      >
        <Tab
          value="columns"
          label="Columns"
          icon={<GridOnIcon fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          value="models"
          label={labelWithTooltip('Models', disabledHint)}
          icon={<ModelTrainingIcon fontSize="small" />}
          iconPosition="start"
          disabled={!hasView}
        />
        <Tab
          value="pivots"
          label={labelWithTooltip('Pivots', disabledHint)}
          icon={<PivotTableChartIcon fontSize="small" />}
          iconPosition="start"
          disabled={!hasView}
        />
      </Tabs>
    </Box>
  );
};

export default ViewSubTabs;
